/** Show the directory's known state while the Agent cannot answer its own pages. */

import { useEffect, useState } from "react";
import { Icon } from "../components/Icon";
import { MarkdownText } from "../components/MarkdownText";
import { hub, hubSettingsHref, instanceStatus, localRuntimeApplied, wakesOnDemand } from "../hub/api";
import { api, hubAgents, type HubAgent, type LastAgentView } from "../lib/api";
import { ErrorState } from "./ui";

/** The supervisor's words for a process that is on its way up. */
const RISING = ["starting", "restarting"];

/** A disconnected socket alone does not prove that a cloud process stopped. */
export function agentNotRunning(agent: HubAgent | null | undefined): boolean {
  if (!agent || agent.online) return false;
  if (agent.location !== "cloud") return true;
  if (agent.desired_running === false) return true;
  if (wakesOnDemand(agent) && !agent.runner?.last_error) return false;
  return Boolean(agent.runner?.last_error) || agent.runner?.state === "stopped" || RISING.includes(agent.runner?.state ?? "");
}

/** The directory's one status for this row (`instanceStatus`), and what this page adds about where to look. */
function words(agent: HubAgent): { title: string; hint?: string } {
  const title = instanceStatus(agent).label;
  if (agent.location === "local" && agent.client_id)
    return { title, hint: agent.client?.online === false ? (agent.client.name || "电脑") + "离线，打开客户端后继续。" : undefined };
  if (agent.location !== "cloud") return { title, hint: agent.paired ? "请在运行它的设备上检查连接。" : undefined };
  return { title };
}

/** Availability comes from the same acknowledged directory row in both workspace states. */
function canStartAgent(agent: HubAgent): boolean {
  const managedLocal = agent.location === "local" && Boolean(agent.client_id);
  const clientOffline = managedLocal && agent.client?.online === false;
  const localStopped = localRuntimeApplied(agent) && agent.local_runtime?.state === "stopped";
  return agent.location === "cloud" && agent.runner?.state === "stopped"
    || managedLocal && agent.desired_running === false && (localStopped || clientOffline);
}

/** The cold page and a retained workspace share one explicit Owner-start action. */
export function AgentStartButton({ agent, onStarted }: {
  agent: HubAgent; onStarted: (agent: HubAgent, failure?: unknown) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const managedLocal = agent.location === "local" && Boolean(agent.client_id);
  const clientOffline = managedLocal && agent.client?.online === false;
  const start = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      onStarted((await hub.start(agent.id)).agent);
    } catch (failure) {
      setError(failure);
      // Enabling can be saved before capacity refuses the actual start.
      // Read that intent once; neither a retry nor a guessed local state.
      const latest = (await hubAgents().catch(() => null))?.find(row => row.id === agent.id);
      if (latest) onStarted(latest, failure);
    } finally {
      setBusy(false);
    }
  };
  if (!canStartAgent(agent)) return null;
  return <><button type="button" className="btn btn-accent" disabled={busy} aria-busy={busy} onClick={() => void start()}>
    {busy ? managedLocal ? "正在保存请求…" : "正在启动…" : clientOffline ? "上线后启动" : "启动"}
  </button>{error ? <ErrorState error={error} /> : null}</>;
}

export function StoppedAgent({
  agent,
  onStarted,
}: {
  agent: HubAgent;
  /** The row the Hub answered the start with, so this page follows the Agent. */
  onStarted: (agent: HubAgent, failure?: unknown) => void;
}) {
  const [view, setView] = useState<LastAgentView | null>(null);
  const [reading, setReading] = useState(true);
  const [readError, setReadError] = useState(false);
  const [reload, setReload] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    setReading(true); setReadError(false); setView(null); setSelected(null);
    void api.lastView(controller.signal).then(value => {
      if (!controller.signal.aborted) setView(value);
    }).catch(() => { if (!controller.signal.aborted) setReadError(true); })
      .finally(() => { if (!controller.signal.aborted) setReading(false); });
    return () => controller.abort();
  }, [agent.id, reload]);
  const said = words(agent);
  const canStart = canStartAgent(agent);
  // Coming up, or going down: nothing for the reader to press, and a button
  // that only repeats what is already happening is worse than none.
  const waiting = agent.location === "cloud" && !canStart && !agent.runner?.last_error && (RISING.includes(agent.runner?.state ?? "")
    || agent.desired_running === false);
  const elsewhere = hubSettingsHref(agent.id);
  const hasRecords = Boolean(view && (view.conversations.length || view.requests.length || view.pending.length
    || view.workspaces.some(workspace => workspace.items.length)));
  return (
    <section className={`agent-unavailable${hasRecords ? "" : " is-empty"}`} data-testid="stopped-agent" aria-labelledby="agent-unavailable-title">
      <header className="agent-unavailable-header">
      <div className="agent-unavailable-mark">
        <Icon name={agent.runner?.last_error ? "alert" : waiting ? "refresh" : "stop"} size={20} />
      </div>
      <div className="agent-unavailable-heading"><h1 id="agent-unavailable-title">{said.title}</h1>
        {said.hint ? <p>{said.hint}</p> : null}
      </div>
      {!waiting ? <div className="agent-unavailable-actions">
        <AgentStartButton agent={agent} onStarted={onStarted} />
        {!canStart || agent.runner?.last_error ? <a className="btn" href={elsewhere}>{agent.location === "local" && !agent.client_id && !agent.paired ? "连接电脑" : "查看状态"}</a> : null}
      </div> : null}
      </header>
      {reading ? <p className="agent-history-notice" role="status">正在读取已有记录…</p>
        : readError ? <p className="agent-history-notice" role="alert">记录暂未读到。<button type="button" className="btn" onClick={() => setReload(value => value + 1)}>重试</button></p>
        : hasRecords && view ? <LastViewContent view={view} agentName={agent.name} selected={selected} onSelect={setSelected} /> : null}
    </section>
  );
}

function date(ts: number) {
  return new Date(ts * 1000).toLocaleString("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function LastViewContent({ view, agentName, selected, onSelect }: {
  view: LastAgentView; agentName: string; selected: string | null; onSelect: (id: string) => void;
}) {
  const conversation = view.conversations.find(item => item.id === selected) ?? view.conversations[0];
  const hasWork = view.workspaces.some(workspace => workspace.items.length);
  const pick = (id: string) => {
    onSelect(id);
    document.getElementById("agent-history-conversation")?.scrollIntoView({ behavior: "auto", block: "start" });
  };
  return <div className="agent-history">
    <p className="agent-history-notice">{view.at ? `上次记录 · ${date(view.at)} · 只读` : "已提交的请求 · 只读"}</p>
    {hasWork ? <section className="agent-history-section" aria-label="最近工作">
      <h2>最近工作</h2>
      {view.workspaces.filter(workspace => workspace.items.length).map(workspace => <div key={workspace.key}>
        <p className="agent-history-source">{workspace.title}<time>{date(workspace.at)}</time></p>
        {workspace.items.map(item => {
          const session = item.context.session_id || item.context.session;
          return <article className="agent-history-work" key={item.id}>
            <div><h3>{item.title}</h3><p>{item.status}</p></div>
            {view.conversations.some(row => row.id === session) ? <button type="button" className="btn btn-quiet" onClick={() => pick(session)}>查看对话<Icon name="arrow" size={14} /></button> : null}
          </article>;
        })}
      </div>)}
    </section> : null}
    {view.pending.length ? <section className="agent-history-section" aria-label="停止前待处理">
      <h2>停止前待处理</h2>
      {view.pending.map(item => <article className="agent-history-work" key={item.key}>
        <div><h3>{item.title}</h3>{item.summary ? <p>{item.summary}</p> : null}</div>
        <button type="button" className="btn btn-quiet" onClick={() => pick(item.session_id)}>查看对话<Icon name="arrow" size={14} /></button>
      </article>)}
    </section> : null}
    {conversation ? <section className="agent-history-section" id="agent-history-conversation" aria-label="最近对话">
      <h2>最近对话</h2>
      {view.conversations.length > 1 ? <nav className="agent-history-picker" aria-label="选择对话">
        {view.conversations.map(item => <button type="button" key={item.id} aria-pressed={item.id === conversation.id} onClick={() => onSelect(item.id)}>{item.title}</button>)}
      </nav> : null}
      {conversation.partial ? <p className="agent-history-notice">这里保留了最近一段对话，启动后可查看完整记录。</p> : null}
      <div className="agent-history-messages">{conversation.messages.map(message => <article className={`agent-history-message is-${message.role === "user" ? "user" : "agent"}`} key={message.id}>
        <header><strong>{message.role === "user" ? "你" : agentName}</strong><time>{date(message.ts)}</time></header>
        <MarkdownText text={message.text} breaks />
      </article>)}</div>
    </section> : view.requests.length ? <section className="agent-history-section" aria-label="最近请求">
      <h2>最近请求</h2>
      <p className="agent-history-notice">回复需启动后读取。</p>
      {view.requests.map(item => <article className="agent-history-message is-user" key={item.id}>
        <header><strong>你</strong><time>{date(item.ts)}</time></header><MarkdownText text={item.text} breaks />
      </article>)}
    </section> : null}
  </div>;
}
